import { parseFillGapsStructuredBody, flattenFillGapParts } from './gapParsing.js';

export const FILL_GAPS_SELF_CHECK_META = '__self_check_meta__';

export function readFillGapsSelfCheckMeta(answersObj) {
    return answersObj?.[FILL_GAPS_SELF_CHECK_META] || {};
}

export function extractGapAnswersOnly(answersObj) {
    if (!answersObj || typeof answersObj !== 'object') return {};
    const { [FILL_GAPS_SELF_CHECK_META]: _m, ...rest } = answersObj;
    return rest;
}

function formatSuggestedForGap(gapPart) {
    if (!gapPart?.answers?.length) return '';
    return gapPart.answers.join(' / ');
}

/** Build per-gap context: text before the gap and the suggested answer from the exercise key. */
export function buildSelfCheckGapContexts(exerciseBody) {
    const parts = parseFillGapsStructuredBody(exerciseBody || '');
    const flat = flattenFillGapParts(parts);
    const contexts = [];
    let gapIdx = 0;
    let textBefore = '';

    for (const p of flat) {
        if (p.type === 'text') {
            textBefore += p.text;
        } else if (p.type === 'gap') {
            contexts.push({
                gapIndex: gapIdx,
                question: textBefore.trim(),
                suggested: formatSuggestedForGap(p),
            });
            textBefore = '';
            gapIdx += 1;
        }
    }
    return contexts;
}

export function mergeAiFeedbackIntoAnswers(answersObj, aiFeedback) {
    const gapAnswers = extractGapAnswersOnly(answersObj);
    const meta = readFillGapsSelfCheckMeta(answersObj);
    return {
        ...gapAnswers,
        [FILL_GAPS_SELF_CHECK_META]: {
            ...meta,
            ai_feedback: aiFeedback,
            ai_feedback_at: new Date().toISOString(),
        },
    };
}

export const SELF_CHECK_FEEDBACK_SYSTEM_PROMPT = `You are an English language teacher reviewing student gap-fill answers in a Self-Check exercise.

For each gap item you receive:
- Question: the text shown before the gap (may be in Russian or English — context for what the student should produce)
- Student answer: what the student typed in the gap
- Suggested answer: the model answer from the exercise key (may list alternatives separated by " / ")

Your job:
1. Compare the student's answer with the suggested answer.
2. Identify what is correct — including valid alternatives that differ from the suggested answer.
3. Identify what is incorrect or could be improved.
4. Explain everything in clear English using bullet points (lines starting with "- ").

Be specific: quote the student's words or phrases when commenting. Accept reasonable paraphrases and alternative word choices when grammatically and semantically correct. For translation tasks, accept valid English that conveys the same meaning even if it differs from the suggested wording.

Return ONLY valid JSON — no markdown fences — in this shape:
{"0": "- first point\\n- second point", "1": "..."}

Keys are gap index strings ("0", "1", …). Each value is a multi-line string of bullet points.`;

export function buildSelfCheckFeedbackUserPrompt(items) {
    return items.map((item, i) => (
        `--- Gap ${i} ---
Question (before gap): ${item.question || '(no preceding text)'}
Student answer: ${item.studentAnswer || '(empty)'}
Suggested answer: ${item.suggested || '(no answer key)'}`
    )).join('\n\n');
}

export function parseSelfCheckFeedbackResponse(content, expectedCount) {
    let parsed;
    try {
        const trimmed = (content || '').trim();
        const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
        parsed = JSON.parse(jsonMatch ? jsonMatch[0] : trimmed);
    } catch {
        if (expectedCount === 1) {
            return { '0': (content || '').trim() };
        }
        throw new Error('AI returned invalid JSON. Please try again.');
    }

    const result = {};
    for (let i = 0; i < expectedCount; i += 1) {
        const key = String(i);
        result[key] = (parsed[key] ?? parsed[i] ?? '').toString().trim();
    }
    return result;
}

export function collectSelfCheckFeedbackItems(exerciseBody, answersObj) {
    const contexts = buildSelfCheckGapContexts(exerciseBody);
    const gapAnswers = extractGapAnswersOnly(answersObj);
    const meta = readFillGapsSelfCheckMeta(answersObj);
    const snap = meta.suggested_answers || {};

    return contexts.map((ctx) => ({
        gapIndex: ctx.gapIndex,
        question: ctx.question,
        studentAnswer: gapAnswers[ctx.gapIndex] ?? gapAnswers[String(ctx.gapIndex)] ?? '',
        suggested: (snap[String(ctx.gapIndex)] !== undefined && snap[String(ctx.gapIndex)] !== '')
            ? snap[String(ctx.gapIndex)]
            : ctx.suggested,
    }));
}
